// Minimal SkioBoxChildProducts component
import { LitElement, html, css } from "https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js";

const boxChildStyles = css`
  :host {
    position: relative;
  }
  .skio-box-child-grid {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 2rem 0;
    z-index: 0;
    max-height: 450px;
    overflow: scroll;
    flex-wrap: wrap;
  }

  .skio-box-child-card {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
    max-width: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
  }
  @media screen and (min-width: 750px) {
    .skio-box-child-card {
      max-width: 33.333%;
    }
  }
  .skio-box-child-card img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    margin-right: 10px;
  }
  .skio-box-child-card-variant-title {
    font-size: 0.8rem;
  }
  .skio-box-child-card label {
    display: flex;
    align-items: center;
    margin-top: 10px;
  }

  .skio-box-child-card button,
  .skio-box-child-card input[type="number"] {
    width: 40px;
    height: 40px;
    border: 1px solid #eee;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1.2rem;
    font-weight: 600;
    color: #222;
    text-align: center;
  }

  .skio-box-child-card button {
    height: 42px;
    width: 42px;
  }

  .skio-box-child-card button:first-of-type {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }

  .skio-box-child-card button:last-of-type {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  }

  .skio-box-child-card input[type="number"] {
    border-radius: 0;
    border-left: none;
    border-right: none;
    height: 40px;
    padding: 0;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .skio-box-progress-bar-wrapper {
    margin-top: 20px;
    background-color: #fff;
  }
  .skio-box-progress-bar-container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto 1.5rem auto;
    background-color: #fff;
    border: 1px var(--skio-progress-bar-color, #000) solid;
    border-radius: 4px;
    border-radius: 0;
    height: 12px;
    position: relative;
  }
  .skio-box-progress-bar {
    height: 100%;
    background-color: var(--skio-progress-bar-color, #000);
    border-radius: 0;
    transition: width 0.3s;
    opacity: 1;
  }

  .skio-box-progress-label {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%,-50%);
    text-align: center;
    width: 100%;
    color: var(--skio-progress-bar-color, #000);
    z-index: 2;
    pointer-events: none;
    text-transform: uppercase;
    font-weight: bold;
    
    opacity: 1;
    display: none;
  }

  
`;

export class SkioBoxChildProducts extends LitElement {
  static styles = boxChildStyles;
  static properties = {
    product: { type: Object },
    productHandle: { type: String },
    variantId: { type: String },
    childProducts: { state: true },
    loading: { state: true },
    error: { state: true },
    selectedVariants: { state: true },
    selectedVariant: { type: Object },
    maxQuantity: { state: true },
    options: { type: Object },
  };

  constructor() {
    super();
    this.product = null;
    this.productHandle = '';
    this.variantId = '';
    this.childProducts = [];
    this.loading = false;
    this.error = '';
    this.selectedVariants = {};
    this.selectedVariant = null;
    this.maxQuantity = 0;
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this._initSelectedVariantFromAttribute();
    this._initProduct().then(() => {
      this._fetchChildProducts();
      this._setupVariantListeners();
      this._setupFormIntegration();
    });
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _initSelectedVariantFromAttribute() {
    // Check if selectedVariant was passed as an attribute from Liquid template
    const selectedVariantAttr = this.getAttribute('selectedVariant');
    if (selectedVariantAttr) {
      try {
        const variant = JSON.parse(selectedVariantAttr);
        if (variant && variant.id) {
          this.selectedVariant = variant;
          this.variantId = variant.id;
        }
      } catch (e) {
        console.warn('Failed to parse selectedVariant attribute:', e);
      }
    }
  }

  async _initProduct() {
    if (this.product) {
      // If product is already provided
      this.variantId = this.product.variants?.[0]?.id || '';
      // Initialize selectedVariant to trigger maxQuantity calculation
      if (this.product.variants?.[0]) {
        this.selectedVariant = this.product.variants[0];
      }
    } else if (this.productHandle) {
      // Fetch product JSON from Shopify
      try {
        const res = await fetch(`/products/${this.productHandle}.js`);
        if (!res.ok) throw new Error('Failed to fetch product');
        this.product = await res.json();
        this.variantId = this.product.variants?.[0]?.id || '';
        // Initialize selectedVariant to trigger maxQuantity calculation
        if (this.product.variants?.[0]) {
          this.selectedVariant = this.product.variants[0];
        }
      } catch (e) {
        this.error = e.message;
      }
    }
    
    // Try to get current variant from form if available
    this._tryGetCurrentVariantFromForm();
  }

  async _fetchChildProducts() {
    if (!this.variantId) return;
    this.loading = true;
    this.error = '';
    try {
      const res = await fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productVariantPlatformId: this.variantId }),
      });
      const data = await res.json();
      if (!res.ok || !data.ClassicBoxV2) throw new Error('Failed to fetch child products');
      this.childProducts = data.ClassicBoxV2.selectableProductVariants || [];
      
      // If preset_box is true, automatically select all child products with quantity 1
      if (this.options?.preset_box && this.childProducts.length > 0) {
        this.selectedVariants = {};
        this.childProducts.forEach(child => {
          const variantId = child.platformId || child.id;
          this.selectedVariants[variantId] = 1;
        });
        this._injectHiddenInputs();
      }
      
      // Ensure maxQuantity is set after child products are loaded
      this._updateMaxQuantity();
    } catch (e) {
      this.error = e.message;
      this.childProducts = [];
    } finally {
      this.loading = false;
    }
  }

  _onVariantChange(e) {
    // Shopify's variant:change event (theme dependent)
    const variant = e.detail?.variant;
    if (variant && variant.id && variant.id !== this.variantId) {
      this.variantId = variant.id;
      this.selectedVariants = {}; // Clear selections
      this._injectHiddenInputs(); // Remove hidden inputs
      this._fetchChildProducts();
    }
  }

  updated(changed) {
    
    if (changed.has('selectedVariant')) {
      this._updateMaxQuantity();
      // After updating maxQuantity, check if we need to clear selections
      const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
      if (!this.maxQuantity || isNaN(this.maxQuantity) || totalSelected > this.maxQuantity) {
        this.selectedVariants = {};
        this._injectHiddenInputs();
      }
      setTimeout(() => {
        this._updateAddToCartButtonState(); // Disable button after DOM updates
      }, 800);
    }
    
    this._updateAddToCartButtonState();
  }

  _updateMaxQuantity() {    
    
    if (this.selectedVariant && this.selectedVariant.title) {
      // Use regex to extract the first number from the variant title
      const numberMatch = this.selectedVariant.title.match(/\d+/);
      let parsed = numberMatch ? parseInt(numberMatch[0], 10) : 0;
      
      if (this.product.handle && this.product.handle.includes('office-coffee-bundle-1')) {
        parsed = Math.floor(parsed / 80);
      }
      
      this.maxQuantity = isNaN(parsed) ? 0 : parsed;
    } else {
      this.maxQuantity = 0;
    }
  }

  _onQuantityChange(e, child) {
    const value = parseInt(e.target.value, 10);
    const variantId = child.platformId || child.id;
    if (value > 0) {
      this.selectedVariants = { ...this.selectedVariants, [variantId]: value };
    } else {
      // Remove if value is 0 or less
      const updated = { ...this.selectedVariants };
      delete updated[variantId];
      this.selectedVariants = updated;
    }
    this._injectHiddenInputs();
  }

  _changeQuantityBy(child, delta) {
    const variantId = child.platformId || child.id;
    const current = this.selectedVariants[variantId] || 0;
    let next = current + delta;
    if (delta > 0 && current === 0) {
      next = 1; // If incrementing from 0, set to 1
    }
    if (next < 0) next = 0;
    // Prevent exceeding max
    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0) - current + next;
    if (this.maxQuantity && totalSelected > this.maxQuantity) {
      next = current; // Don't allow increment if it would exceed max
    }
    const fakeEvent = { target: { value: next } };
    this._onQuantityChange(fakeEvent, child);
    this._injectHiddenInputs();
  }

  _setupVariantListeners() {
    // Try to find the product form and variant input
    let form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
    let variantInput = form ? form.querySelector('[name="id"]') : document.querySelector('[name="id"]');
    if (variantInput && this.product && Array.isArray(this.product.variants)) {
      variantInput.addEventListener('change', e => {
        const variant = this.product.variants.find(v => v.id == e.target.value);
        if (variant) {
          this.selectedVariant = variant;
        }
      });
    }
    // Listen for global variantChanged event (Skio convention)
    document.addEventListener('variantChanged', e => {
      if (!this.product || !Array.isArray(this.product.variants)) return;
      let variantId = e.detail.variantId;
      let variant = this.product.variants.find(x => x.id == variantId);
      if (variant) {
        this.selectedVariant = variant;
      }
    });
  }

  _tryGetCurrentVariantFromForm() {
    // Try to get the currently selected variant from the form
    const form = this.findProductForm();
    if (form && this.product && Array.isArray(this.product.variants)) {
      const variantId = this.getVariantId(form);
      if (variantId) {
        const variant = this.product.variants.find(v => v.id == variantId);
        if (variant) {
          this.selectedVariant = variant;
          this.variantId = variantId;
        }
      }
    }
  }

  _setupFormIntegration() {
    // Always use the main product form for theme-agnostic support
    this._form = document.querySelector('form[action*="/cart/add"]');
  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents();
    if (formFromParent) return formFromParent;
    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms();
    return allProductForms[0] || null;
  }

  findFormThroughParents() {
    let currentElement = this;
    let depth = 0;
    const maxDepth = 5;
    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'));
      const validForm = forms.find(form => this.isValidProductForm(form));
      if (validForm) return validForm;
      currentElement = currentElement.parentElement;
      depth++;
    }
    return null;
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form);
      return this.isValidProductForm(form) && hasVariantId;
    });
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add');
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]');
    return hasCartAction && hasSubmitButton;
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form);
    const idFromFormData = formData.get('id');
    if (idFromFormData) return Number(idFromFormData);
    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]');
    return idInput ? Number(idInput.value) : null;
  }
 
  _injectHiddenInputs() {
    const form = this.findProductForm();
    if (!form) return;
    // Remove any previous hidden inputs for child variants
    Array.from(form.querySelectorAll('input[data-skio-child-variant]')).forEach(input => input.remove());
    // Add hidden inputs for each selected child variant
    for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
      if (qty > 0) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[_pv${variantId}]`;
        input.value = qty;
        input.setAttribute('data-skio-child-variant', 'true');
        form.appendChild(input);
        // Add hidden input for product title
        const child = this.childProducts.find(c => (c.platformId || c.id) == variantId);

        if (child) {
          console.log('variantId ' + variantId);
          const titleInput = document.createElement('input');
          titleInput.type = 'hidden';
          titleInput.name = `properties[${child.productTitle}]`;                  
          titleInput.value = qty;
          titleInput.setAttribute('data-skio-child-variant', 'true');
          form.appendChild(titleInput);
        }
      }
    }
    this._updateAddToCartButtonState();
  }

  _updateAddToCartButtonState() {
    const form = this.findProductForm();
    if (!form) return;
    const addToCartBtn = form.querySelector('button[type="submit"], [type="submit"]');
    if (!addToCartBtn) return;
    
    // If preset_box is true, always enable the add to cart button
    if (this.options?.preset_box) {
      addToCartBtn.disabled = false;
      return;
    }
    
    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
    const shouldEnable = this.maxQuantity && totalSelected === this.maxQuantity;
    addToCartBtn.disabled = !shouldEnable;
    
  }

  render() {
    // If preset_box is true, don't show the UI
    if (this.options?.preset_box) {
      return html``;
    }
    
    if (!this.childProducts.length) {
      return html``;
    }
    
    if (this.loading) {
      return html`<div style="text-align:center;">Loading options...</div>`;
    }
    
    const totalSelected = Object.values(this.selectedVariants).reduce((sum, q) => sum + q, 0);
    const max = this.maxQuantity;
    const percent = max ? Math.min(100, Math.round((totalSelected / max) * 100)) : 0;

    return html`
      <div class="skio-box-progress-bar-wrapper">
        <div class="skio-box-progress-bar-container">
          <div 
            class="skio-box-progress-bar"
            style="width: ${percent}%;"
            title="Select all required items to continue"
          ></div>
          ${totalSelected < max ? html`<div class="skio-box-progress-label">Add ${max - totalSelected} to continue</div>` : html`<div class="skio-box-progress-label">Continue</div>`}
        </div>
      </div>
      <div class="skio-box-child-grid">
        ${this.childProducts.map(child => {
          const variantId = child.platformId || child.id;
          const quantity = this.selectedVariants[variantId] || 0;
          const disableUp = max && totalSelected >= max;
          return html`
            <div class="skio-box-child-card">
              <img src="${child.imageSrc}" alt="${child.productTitle}" />
              <div class="skio-box-child-card-title-container">
                <div class="skio-box-child-card-title">${child.productTitle}</div>
                ${child.productVariantTitle && !child.productVariantTitle.includes('Default Title') ? html`<div class="skio-box-child-card-variant-title">${child.productVariantTitle}</div>` : ''}
              </div>
              <div>
                <label>
                  <button type="button" @click="${() => this._changeQuantityBy(child, -1)}">-</button>
                  <input type="number" min="0" step="1" .value="${quantity}" @input="${e => this._onQuantityChange(e, child)}" @change="${e => this._onQuantityChange(e, child)}" />
                  <button type="button" @click="${() => this._changeQuantityBy(child, 1)}" ?disabled="${disableUp}">+</button>
                </label>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}

customElements.define('skio-static-build-a-box', SkioBoxChildProducts);